/**
 * Dual-auth hook for admin pages.
 *
 * - If a local admin JWT is stored (localStorage.emina_local_admin_token), it takes precedence.
 * - Otherwise falls through to Auth0.
 *
 * Exposes the same surface as useAuth0 for drop-in replacement in admin pages:
 *   getAccessTokenSilently, isAuthenticated, isLoading, user, loginWithRedirect, logout
 */
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { useMemo } from 'react';

const LOCAL_KEY = 'emina_local_admin_token';

export function useAdminAuth() {
  const a0 = useAuth0();
  const navigate = useNavigate();
  const localToken = typeof window !== 'undefined' ? localStorage.getItem(LOCAL_KEY) : null;

  return useMemo(() => {
    // authFetch wraps `fetch` with the proper Authorization Bearer header
    // so admin pages don't have to repeat the token-then-fetch dance.
    const makeAuthFetch = (getToken) => async (url, init = {}) => {
      const token = await getToken();
      const headers = new Headers(init.headers || {});
      if (token) headers.set('Authorization', `Bearer ${token}`);
      return fetch(url, { ...init, headers });
    };

    if (localToken) {
      let payload = {};
      try {
        payload = JSON.parse(atob(localToken.split('.')[1] || ''));
      } catch {}
      const getToken = async () => localToken;
      return {
        isAuthenticated: true,
        isLoading: false,
        user: { email: payload.email, name: payload.name, sub: payload.sub, picture: '' },
        getAccessTokenSilently: getToken,
        authFetch: makeAuthFetch(getToken),
        loginWithRedirect: () => navigate('/admin/login'),
        logout: () => { localStorage.removeItem(LOCAL_KEY); navigate('/admin/login'); },
        isLocal: true,
      };
    }
    return {
      ...a0,
      getAccessTokenSilently: a0.getAccessTokenSilently,
      authFetch: makeAuthFetch(a0.getAccessTokenSilently || (async () => null)),
      isLocal: false,
    };
  }, [localToken, a0, navigate]);
}

export function getLocalAdminToken() {
  return typeof window !== 'undefined' ? localStorage.getItem(LOCAL_KEY) : null;
}

export function clearLocalAdminToken() {
  try { localStorage.removeItem(LOCAL_KEY); } catch {}
}
